import type { StructureEntryCandidateLeg, StructureEntryEvaluationSpec, UserPrefs, WatchRule } from "@/shared/lib/types";

const OCC_CONTRACT_RE = /^(?:O:)?([A-Z]{1,6})(\d{2})(\d{2})(\d{2})([CP])(\d{8})$/i;

function parseOccContract(optionContract?: string) {
  const value = optionContract?.trim();
  if (!value) return null;

  const match = value.match(OCC_CONTRACT_RE);
  if (!match) return null;

  const [, root, yy, mm, dd, side, strikeRaw] = match;
  const year = 2000 + Number(yy);
  const month = Number(mm);
  const day = Number(dd);
  const strike = Number(strikeRaw) / 1000;

  if (!year || !month || !day || !Number.isFinite(strike)) return null;

  return {
    root: root.toUpperCase(),
    expiry: new Date(year, month - 1, day),
    side: side.toUpperCase() === "C" ? "C" : "P",
    strike,
  };
}

function formatShortExpiry(date: Date) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

function formatStrike(strike: number) {
  return Number.isInteger(strike)
    ? `$${strike.toLocaleString("en-US")}`
    : `$${strike.toLocaleString("en-US", { maximumFractionDigits: 3 })}`;
}

function formatOptionContract(optionContract?: string) {
  const parsed = parseOccContract(optionContract);
  if (!parsed) return null;
  return `${parsed.root} ${formatShortExpiry(parsed.expiry)} ${formatStrike(parsed.strike)} ${parsed.side === "C" ? "Call" : "Put"}`;
}

function formatPositionStrikes(legs: Array<{ option_contract?: string }>) {
  const parsed = legs
    .map((leg) => parseOccContract(leg.option_contract))
    .filter((leg): leg is NonNullable<typeof leg> => leg !== null)
    .sort((a, b) => a.strike - b.strike || a.side.localeCompare(b.side));
  if (!parsed.length) return null;

  return parsed
    .map((leg) => `${formatStrike(leg.strike)}${leg.side}`)
    .join(" · ");
}

function formatStructureEntryStrike(value?: string) {
  const strike = Number(value);
  if (!Number.isFinite(strike) || strike <= 0) return null;
  return `$${strike.toLocaleString("en-US", { maximumFractionDigits: 3 })}`;
}

function describeStructureEntryTopology(legs: StructureEntryCandidateLeg[]) {
  if (legs.length === 2) {
    const optionTypes = new Set(legs.map((leg) => leg.option_type?.toLowerCase()));
    const sides = new Set(legs.map((leg) => leg.side?.toUpperCase()));
    if (optionTypes.size === 1 && sides.has("LONG") && sides.has("SHORT")) {
      const optionType = legs[0]?.option_type?.toLowerCase();
      return optionType === "call" ? "Call vertical" : optionType === "put" ? "Put vertical" : "Vertical spread";
    }
  }

  if (legs.length === 4) {
    const puts = legs.filter((leg) => leg.option_type?.toLowerCase() === "put");
    const calls = legs.filter((leg) => leg.option_type?.toLowerCase() === "call");
    const shortPut = puts.find((leg) => leg.side?.toUpperCase() === "SHORT");
    const longPut = puts.find((leg) => leg.side?.toUpperCase() === "LONG");
    const shortCall = calls.find((leg) => leg.side?.toUpperCase() === "SHORT");
    const longCall = calls.find((leg) => leg.side?.toUpperCase() === "LONG");
    const shortPutStrike = Number(shortPut?.strike);
    const longPutStrike = Number(longPut?.strike);
    const shortCallStrike = Number(shortCall?.strike);
    const longCallStrike = Number(longCall?.strike);
    if ([shortPutStrike, longPutStrike, shortCallStrike, longCallStrike].every(Number.isFinite)) {
      if (shortPutStrike === shortCallStrike) return "Iron butterfly";
      if (longPutStrike < shortPutStrike && shortCallStrike < longCallStrike) return "Iron condor";
    }
  }

  return legs.length > 0 ? `${legs.length}-leg options setup` : "Options setup";
}

function getStructureEntryCandidate(watch?: WatchRule | null) {
  if (!watch) return null;
  const spec = watch.evaluation_spec;
  if (watch.intent !== "structure_entry" || spec?.subject !== "STRUCTURE_ENTRY") return null;
  const candidate = (spec as StructureEntryEvaluationSpec).candidate;
  const legs = (candidate?.legs ?? [])
    .map((leg) => ({ ...leg, option_type: leg.option_type?.toLowerCase() }))
    .filter((leg) =>
      (leg.side === "LONG" || leg.side === "SHORT") &&
      (leg.option_type === "call" || leg.option_type === "put") &&
      Boolean(formatStructureEntryStrike(leg.strike)),
    );
  if (!candidate?.expiry || legs.length === 0) return null;
  return { candidate, legs };
}

export function isStructureEntryWatch(watch?: WatchRule | null) {
  return watch?.intent === "structure_entry" && watch.evaluation_spec?.subject === "STRUCTURE_ENTRY";
}

export function isSlotConsumingWatch(status?: string) {
  const normalized = status?.toLowerCase();
  return normalized === "active";
}

export function isWatchExpired(watch: WatchRule) {
  return (
    (watch?.status || "").toLowerCase() === "expired" ||
    (typeof watch?.expires_at === "number" &&
      watch.expires_at > 0 &&
      watch.expires_at * 1000 <= Date.now())
  );
}

export function hasAnyConfiguredChannel(prefs: UserPrefs | null) {
  if (!prefs) return false;
  const channels = prefs.notification_channels ?? [];
  if (channels.some((channel) => channel.enabled && channel.value)) {
    return true;
  }

  return Object.values(prefs.channels ?? {}).some((channel) => {
    if (!channel?.enabled) return false;
    return Boolean(
      channel.value ||
        channel.webhook_url ||
        channel.chat_id ||
        channel.address ||
        channel.phone_number,
    );
  });
}

export function getWatchContractDisplay(watch?: WatchRule | null) {
  const structureEntry = getStructureEntryCandidate(watch);
  if (structureEntry) {
    const strikes = [...structureEntry.legs]
      .sort((a, b) => Number(a.option_type === "call") - Number(b.option_type === "call") || Number(a.strike) - Number(b.strike))
      .map((leg) => {
        const strike = formatStructureEntryStrike(leg.strike);
        return strike ? `${strike}${leg.option_type?.toLowerCase() === "call" ? "C" : "P"}` : null;
      })
      .filter((value): value is string => value !== null)
      .join(" · ");
    const topology = describeStructureEntryTopology(structureEntry.legs).replace(/\b\w/g, (letter) => letter.toUpperCase());
    return `${topology} · ${strikes}`;
  }
  const projectedLegs = watch?.position?.legs || [];
  if (projectedLegs.length > 0) {
    const label = String(watch?.position?.strategy || "Held options position").toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
    const strikes = projectedLegs.map((leg) => {
      const type = leg.option?.option_type?.toLowerCase();
      const strike = formatStructureEntryStrike(leg.option?.strike);
      return strike && (type === "call" || type === "put") ? `${strike}${type === "call" ? "C" : "P"}` : "Option details unavailable";
    });
    return `${label} · ${strikes.join(" · ")}`;
  }
  const positionLegs = watch?.position?.position_basis?.legs || [];
  if (positionLegs.length > 0) {
    const strategy = watch?.position?.strategy
      ? String(watch.position.strategy).replace(/_/g, " ")
      : "Multi-leg position";
    const label = strategy.replace(/\b\w/g, (letter) => letter.toUpperCase());
    return `${label} · ${formatPositionStrikes(positionLegs) || `${positionLegs.length} legs`}`;
  }
  const optionContract = watch?.option_contract?.trim();
  if (optionContract) return formatOptionContract(optionContract) || "Selected option";

  const watchIntent = watch?.intent;
  if (watchIntent === "price_target") {
    const targetPrice = watch?.intent_params?.target_price;
    if (targetPrice !== undefined && targetPrice !== null && targetPrice !== "") {
      const numeric = Number(targetPrice);
      return Number.isFinite(numeric) ? `$${numeric.toFixed(2)}` : String(targetPrice);
    }
  }

  if (watchIntent === "iv_rank_threshold") {
    const threshold = watch?.intent_params?.threshold;
    if (threshold !== undefined && threshold !== null && threshold !== "") {
      const numeric = Number(threshold);
      return Number.isFinite(numeric) ? `IV ${numeric}` : `IV ${String(threshold)}`;
    }
  }

  return "N/A";
}

export function getWatchContractShortLabel(watch?: WatchRule | null) {
  return getWatchContractDisplay(watch);
}

export function getWatchExpirationLabel(watch?: WatchRule | null) {
  const structureEntry = getStructureEntryCandidate(watch);
  if (structureEntry) {
    const match = structureEntry.candidate.expiry?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      return formatShortExpiry(new Date(Number(year), Number(month) - 1, Number(day)));
    }
  }
  const projectedExpiry = watch?.position?.legs?.[0]?.option?.expiry;
  if (projectedExpiry && /^\d{4}-\d{2}-\d{2}$/.test(projectedExpiry)) return formatShortExpiry(new Date(`${projectedExpiry}T12:00:00`));
  const positionContract = watch?.position?.position_basis?.legs?.[0]?.option_contract;
  if (positionContract) {
    const parsedPositionContract = parseOccContract(positionContract);
    if (parsedPositionContract) return formatShortExpiry(parsedPositionContract.expiry);
  }
  const parsed = parseOccContract(watch?.option_contract);
  if (parsed) return formatShortExpiry(parsed.expiry);

  if (typeof watch?.expires_at === "number" && watch.expires_at > 0) {
    return formatShortExpiry(new Date(watch.expires_at * 1000));
  }

  return "N/A";
}

export function getWatchExpirationPrefix(watch?: WatchRule | null) {
  return getStructureEntryCandidate(watch) || watch?.position?.legs?.[0]?.option?.expiry || watch?.position?.position_basis?.legs?.[0]?.option_contract || watch?.option_contract
    ? "Contracts expire"
    : "Watch ends";
}

export function getWatchContractTitle(watch?: WatchRule | null) {
  const display = getWatchContractDisplay(watch);
  const expiry = getWatchExpirationLabel(watch);
  return expiry !== "N/A" ? `${display} - ${getWatchExpirationPrefix(watch).toLowerCase()} ${expiry}` : display;
}

export function buildCreateSuccessMessage(
  body: Record<string, unknown>,
  preview?: { recipe?: { label?: string } },
) {
  const position = body.position as { position_basis?: { legs?: unknown[] } } | undefined;
  if (position?.position_basis?.legs?.length) {
    const conditions = body.conditions as Array<{ operator?: string; threshold?: unknown }> | undefined;
    const pnl = conditions?.find((condition) => condition.operator === "gte" || condition.operator === "lte");
    const threshold = Math.abs(Number(pnl?.threshold));
    const direction = pnl?.operator === "lte" ? "falls to" : "reaches";
    return `We'll alert you when this ${position.position_basis.legs.length}-leg position's estimated P&L ${direction} $${Number.isFinite(threshold) ? threshold.toFixed(2) : "0.00"}.`;
  }
  const intentParams = body.intent_params as Record<string, unknown> | undefined;
  const selectedTarget = body.option_contract ? "your selected contract" : body.ticker;
  switch (body.intent) {
    case "good_time_to_buy":
      return `We'll let you know if ${selectedTarget} lines up as a buy setup and clears your enabled filters.`;
    case "good_time_to_sell":
      return `We'll let you know if ${selectedTarget} lines up as a sell setup and clears your enabled filters.`;
    case "iv_rank_threshold":
      return `We'll let you know if IV rank on ${selectedTarget} moves above ${intentParams?.threshold}.`;
    case "price_target": {
      const direction = intentParams?.direction === "below" ? "falls to" : "rises to";
      return `We'll let you know if ${body.ticker} ${direction} $${Number(intentParams?.target_price || 0).toFixed(2)}.`;
    }
    case "volatility_spike":
      return `We'll let you know if ${selectedTarget} hits one of your enabled volatility spike triggers.`;
    default:
      return `We'll let you know if ${preview?.recipe?.label || "this watch"} triggers.`;
  }
}
